import { Injectable } from '@angular/core';
import { BestPrice, Line, Room } from '../models';
import { ICombineEngine } from '@app/shared';

export interface SearchCriteria {
    adults: number;
    children: number;
}

@Injectable({
    providedIn: 'root'
})
export class RoomSortingService {
    constructor() { }

    /**
     * Trie les BestPrice selon les critères d'occupation et de prix
     * @param rooms Liste des BestPrice à trier
     * @param searchCriteria Critères de recherche (nombre d'adultes et d'enfants)
     * @returns Liste triée des BestPrice
     */
    sortBestRates(rooms: BestPrice[], searchCriteria: SearchCriteria): BestPrice[] {
        return rooms
            .filter(room => room.combine !== null)
            .sort((a, b) => {
                return this._sort(a, b, searchCriteria);
            });
    }

    /**
     * Trie les chambres selon les critères d'occupation et de prix
     * @param rooms Liste des chambres à trier
     * @param searchCriteria Critères de recherche (nombre d'adultes et d'enfants)
     * @returns Liste triée des chambres
     */
    sortRealRooms(rooms: Room[], searchCriteria: SearchCriteria): Room[] {
        return rooms
            // .filter(line => line.engineSelected !== null && line.engineSelected.combine !== null)
            .sort((a, b) => {
                return this._sort(a, b, searchCriteria);
            });
    }

    /**
     * Trie les Lines selon les critères d'occupation et de prix
     * @param rooms Liste des Lines à trier
     * @param searchCriteria Critères de recherche (nombre d'adultes et d'enfants)
     * @returns Liste triée des Lines
     */
    sortLines(lines: Line[], searchCriteria: SearchCriteria): Line[] {
        return lines
            // .filter(line => line.engineSelected !== null && line.engineSelected.combine !== null)
            .sort((a, b) => {
                return this._sort(a, b, searchCriteria);
            });
    }

    /**
     * Trie les chambres selon les critères d'occupation et de prix
     * @param a BestPrice | Room | Line
     * @param b BestPrice | Room | Line
     * @param searchCriteria SearchCriteria
     * @returns number
     */
    private _sort(a: BestPrice | Room | Line, b: BestPrice | Room | Line, searchCriteria: SearchCriteria): number {

        // Gérer le cas où le stock (Dispo) est 0
        if (a instanceof Room && b instanceof Room) {
            const aHasDispo = this._hasAnyVisibleLine(a, searchCriteria);
            const bHasDispo = this._hasAnyVisibleLine(b, searchCriteria);

            if (aHasDispo && !bHasDispo) return -1;
            if (!aHasDispo && bHasDispo) return 1;

            const aDispo = a.lines.filter(line => line.engineSelected !== null && line.engineSelected.combine !== null).length;
            const bDispo = b.lines.filter(line => line.engineSelected !== null && line.engineSelected.combine !== null).length;
            if (a.stock === 0 || b.stock === 0) {
                if (aDispo === 0 && bDispo === 0) return 0;
                if (aDispo === 0) return 1;  // a va en bas
                if (bDispo === 0) return -1; // b va en bas
            };

            if (a.stock === 0 && b.stock === 0) return 0;
            if (a.stock === 0) return 1;  // a va en bas
            if (b.stock === 0) return -1; // b va en bas

        }

        const aEngineSelected = this._getCombine(a);
        const bEngineSelected = this._getCombine(b);

        const aCanAccommodate = this._canAccommodateOccupancy(aEngineSelected, searchCriteria);
        const bCanAccommodate = this._canAccommodateOccupancy(bEngineSelected, searchCriteria);

        // 1. Priorité aux chambres qui peuvent accommoder l'occupation
        if (aCanAccommodate && !bCanAccommodate) return -1;
        if (!aCanAccommodate && bCanAccommodate) return 1;

        // 2. Si les deux peuvent ou ne peuvent pas accommoder, trier par prix

        const aTtc = aEngineSelected && aEngineSelected.combine ? aEngineSelected.combine.ttc : 0;
        const bTtc = bEngineSelected && bEngineSelected.combine ? bEngineSelected.combine.ttc : 0;

        // Gérer le cas où le prix est 0
        if (aTtc === 0 && bTtc === 0) return 0;
        if (aTtc === 0) return 1;  // a va en bas
        if (bTtc === 0) return -1; // b va en bas

        if (aTtc !== bTtc && (aCanAccommodate || bCanAccommodate)) {
            return aTtc - bTtc; // Prix croissant
        }

        // Si les deux peuvent accommoder ou aucune ne peut, trier par score d'occupation
        const aScore = this.getOccupancyScore(aEngineSelected, searchCriteria);
        const bScore = this.getOccupancyScore(bEngineSelected, searchCriteria);

        return bScore - aScore;
    }

    /**
     * Renvoie le CombineEngine lié à l'objet (Room, Line ou BestPrice)
     * @param item BestPrice | Room | Line
     * @returns ICombineEngine | null
     */
    private _getCombine(item): ICombineEngine | null {
        if (item instanceof BestPrice) {
            // console.log('item est de type BestPrice');
            return item;
        } else if (item instanceof Room) {
            // console.log('item est de type Room');
            return item['engineSelected'];
        } else if (item instanceof Line) {
            // console.log('item est de type Line');
            return item.engineSelected;
        }
    }

    /**
     * Vérifie si une chambre peut accommoder l'occupation demandée
     */
    private _canAccommodateOccupancy(room: BestPrice | ICombineEngine, searchCriteria: SearchCriteria): boolean {
        if (!room || !room.combine) return false;

        return this.canAccommodateOccupancy(searchCriteria.adults, searchCriteria.children, room.combine.code_occupancy, room.combine.code_children);
    }

    /**
     * Checks if an engine can accommodate a given number of adults and children.
     * The engine is considered able to accommodate the request if:
     * - the total number of people (adults + children) is less than or equal to the total capacity of the engine
     * - the number of adults is less than or equal to the number of adults the engine can accommodate
     * - the number of children is less than or equal to the number of children the engine can accommodate, taking into account any unused adult spaces that can be converted into children spaces.
     *
     * @param requestedAdults - Number of requested adult spaces
     * @param requestedChildren - Number of requested children spaces
     * @param engineAdults - Maximum number of adults the engine can accommodate
     * @param engineChildren - Maximum number of children the engine can accommodate
     * @returns boolean indicating if the engine can accommodate the requested occupancy
     */
    public canAccommodateOccupancy(requestedAdults: number, requestedChildren: number, engineAdults: number, engineChildren: number): boolean {
        // Total engine capacity
        const totalEngineCapacity: number = engineAdults + engineChildren;

        // Total number of requested people
        const totalRequested: number = requestedAdults + requestedChildren;

        // Check if total capacity is sufficient
        if (totalRequested > totalEngineCapacity) {
            return false;
        }

        // Check if there's enough space for adults
        if (requestedAdults > engineAdults) {
            return false;
        }

        // Calculate how many adult spaces can be converted to children spaces
        const unusedAdultSpaces: number = engineAdults - requestedAdults;
        const availableChildrenSpaces: number = engineChildren + unusedAdultSpaces;

        // Check if we can accommodate all children
        return requestedChildren <= availableChildrenSpaces;
    }


    /**
     * Calcule un score d'occupation pour une chambre
     * Plus la capacité est grande, plus le score est élevé
     */
    private getOccupancyScore(room: BestPrice | ICombineEngine, searchCriteria: SearchCriteria): number {
        if (!room || !room.combine) return -1;

        const roomAdults = room.combine.code_occupancy;
        const roomChildren = room.combine.code_children;
        const searchAdults = searchCriteria.adults;
        const searchChildren = searchCriteria.children;

        // Vérifier si la chambre peut accommoder la recherche de base
        // if (roomAdults < searchAdults) return -1;

        // Calculer le score basé sur la capacité totale
        // Plus la capacité est grande, plus le score sera élevé
        const baseScore = 1000; // Score de base pour une chambre valide
        const extraCapacityScore = (roomAdults - searchAdults) * 100 +
            (roomChildren - searchChildren) * 50;

        return baseScore + extraCapacityScore;
    }

    private _hasAnyVisibleLine(room: Room, searchCriteria: SearchCriteria): boolean {
        if (!room || !Array.isArray(room.lines)) {
            return false;
        }
        return room.lines.some((line: Line) => {
            if (!line.isDispo) {
                return false;
            }
            if (line.hasEngine('so')) {
                if (!searchCriteria || !line.occupation) {
                    return true;
                }
                const parts = line.occupation.split('_');
                return parseInt(parts[0]) === searchCriteria.adults
                    && parseInt(parts[1]) === searchCriteria.children;
            }
            return true;
        });
    }
}